import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import RatingJobMonitor from '../../components/admin/RatingJobMonitor';
import RatingJobsList from '../../components/admin/RatingJobsList';
import CreateInterviewModal from './CreateInterviewModal';
import {
  DocumentIcon,
  StarIcon,
  EyeIcon,
  TrashIcon,
  CloudArrowUpIcon,
  FolderIcon,
  TableCellsIcon,
  Squares2X2Icon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  XMarkIcon,
  EnvelopeIcon,
  LinkIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';

const ResumeManagement = () => {
  const [selectedJob, setSelectedJob] = useState(() => {
    // Initialize from localStorage if available
    return localStorage.getItem('selectedJobId') || '';
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'card' or 'table'
  const [sortByCriterion, setSortByCriterion] = useState(''); // For sorting by specific criterion
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingResume, setViewingResume] = useState(null);
  const [selectedResumes, setSelectedResumes] = useState([]);
  // Removed rating weights - new system uses simple sum of 7 parameters (1-10 each)
  const [isRating, setIsRating] = useState(false);
  const [currentRatingJob, setCurrentRatingJob] = useState(null);
  const [showJobMonitor, setShowJobMonitor] = useState(false);
  const [showJobsList, setShowJobsList] = useState(false);
  const [syncMode, setSyncMode] = useState(false);
  
  // Shortlisted candidate management states
  const [showShortlistedModal, setShowShortlistedModal] = useState(false);
  const [shortlistedCandidates, setShortlistedCandidates] = useState([]);
  const [isLoadingShortlisted, setIsLoadingShortlisted] = useState(false);
  const [shortlistedSearch, setShortlistedSearch] = useState('');
  const [isEmailOperationLoading, setIsEmailOperationLoading] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [availableInterviews, setAvailableInterviews] = useState([]);
  const [selectedInterview, setSelectedInterview] = useState('');
  const [selectedCandidatesForScheduling, setSelectedCandidatesForScheduling] = useState([]);
  
  // Interview links states
  const [showGenerateLinksModal, setShowGenerateLinksModal] = useState(false);
  const [selectedInterviewForLinks, setSelectedInterviewForLinks] = useState('');
  const [linkExpiryHours, setLinkExpiryHours] = useState(24);
  const [generatedLinks, setGeneratedLinks] = useState([]);
  const [isGeneratingLinks, setIsGeneratingLinks] = useState(false);
  const [candidatesNeedingLinksCount, setCandidatesNeedingLinksCount] = useState(0);
  const [candidatesNeedingLinks, setCandidatesNeedingLinks] = useState([]);
  
  // Single candidate link states
  const [showSingleLinkModal, setShowSingleLinkModal] = useState(false);
  const [selectedCandidateForLink, setSelectedCandidateForLink] = useState(null);
  const [singleLinkInterview, setSingleLinkInterview] = useState('');
  const [singleLinkExpiry, setSingleLinkExpiry] = useState(24);
  const [candidateLinks, setCandidateLinks] = useState([]);
  
  // Delete confirmation states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resumeToDelete, setResumeToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Create interview modal state
  const [showCreateInterviewModal, setShowCreateInterviewModal] = useState(false);
  
  // Loading state for candidates needing links check
  const [isLoadingCandidatesNeeding, setIsLoadingCandidatesNeeding] = useState(false);
  
  // Cache for interview links to avoid repeated API calls
  const [interviewLinksCache, setInterviewLinksCache] = useState({});
  
  // Weighted rating system states
  const [useWeightedRating, setUseWeightedRating] = useState(false);
  const [weights, setWeights] = useState({
    qualification_weight: 1.0,
    yoe_weight: 1.0,
    skill_match_weight: 1.0,
    quality_experience_weight: 1.0,
    industry_fit_weight: 1.0,
    job_stability_weight: 1.0,
    experience_suitability_weight: 1.0
  });
  
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const queryClient = useQueryClient();

  // Refresh function to reload data
  const handleRefresh = () => {
    queryClient.invalidateQueries(['resumes', selectedJob]);
    queryClient.invalidateQueries(['rating-jobs']);
    queryClient.invalidateQueries(['job-descriptions']);
    toast.success('Data refreshed successfully!');
  };

  // Fetch job descriptions
  const { data: jobsData } = useQuery({
    queryKey: ['job-descriptions'],
    queryFn: () => apiService.getJobDescriptions(),
  });

  // Fetch resumes (without backend filtering)
  const { data: resumesData, isLoading } = useQuery({
    queryKey: ['resumes', selectedJob],
    queryFn: () => apiService.getResumes(selectedJob, 1, 100),
    enabled: !!selectedJob,
  });

  // Fetch active rating jobs to track async processing
  const { data: ratingJobsData } = useQuery({
    queryKey: ['rating-jobs'],
    queryFn: () => apiService.getRatingJobs(1, 100),
    refetchInterval: isRating || showJobMonitor ? 5000 : false, // Only poll when needed
  });

  // Helper function to determine if resume is in async processing
  const getResumeAsyncStatus = (resume) => {
    if (resume.status !== 'pending') return { isAsync: false, status: resume.status };
    
    const activeJobs = ratingJobsData?.data?.jobs?.filter(job => 
      job.job_description_id === selectedJob &&
      ['pending', 'in_progress'].includes(job.status) &&
      job.resume_ids?.includes(resume.id)
    ) || [];
    
    return {
      isAsync: activeJobs.length > 0,
      status: activeJobs.length > 0 ? 'async_processing' : 'pending',
      activeJobs
    };
  };

  // Enhanced client-side filtering with async status consideration
  const filteredResumes = (() => {
    let resumes = resumesData?.resumes?.filter(resume => {
      if (!filterStatus) return true;
      
      const asyncStatus = getResumeAsyncStatus(resume);
      
      // Handle special async processing filter
      if (filterStatus === 'async_processing') {
        return asyncStatus.isAsync;
      }
      
      // Handle regular pending (not in async processing)
      if (filterStatus === 'pending') {
        return resume.status === 'pending' && !asyncStatus.isAsync;
      }
      
      // Handle extraction failed filter
      if (filterStatus === 'extraction_failed') {
        return resume.extraction_status && resume.extraction_status !== 'success' && resume.extraction_status !== 'pending';
      }
      
      // Handle other statuses normally
      return resume.status === filterStatus;
    }) || [];

    // Apply criterion-based sorting
    if (sortByCriterion) {
      resumes = [...resumes].sort((a, b) => {
        const scoreA = a[sortByCriterion] || 0;
        const scoreB = b[sortByCriterion] || 0;
        return scoreB - scoreA; // Sort in descending order (highest first)
      });
    } else {
      // Default sorting by overall score if available
      resumes = [...resumes].sort((a, b) => {
        const scoreA = a.score || 0;
        const scoreB = b.score || 0;
        return scoreB - scoreA;
      });
    }

    return resumes;
  })();

  // Upload resumes mutation
  const uploadResumesMutation = useMutation({
    mutationFn: (formData) => apiService.uploadResumes(formData),
    onSuccess: (response) => {
      const results = response?.results || [];
      const successCount = response?.successful || 0;
      const failedCount = response?.failed || 0;
      
      // Show detailed results for failed uploads
      const failedUploads = results.filter(r => !r.success);
      
      if (failedCount > 0) {
        // Show specific error messages for each failed upload
        failedUploads.forEach(upload => {
          try {
            const filename = upload?.filename || 'Unknown file';
            const message = upload?.message || 'Upload failed';
            const extractionStatus = upload?.extraction_status;
            
            if (extractionStatus === 'failed' || extractionStatus === 'empty' || extractionStatus === 'corrupted') {
              toast.error(`${filename}: ${message}`, {
                duration: 6000,
                style: {
                  maxWidth: '500px',
                }
              });
            } else {
              toast.error(`${filename}: ${message}`);
            }
          } catch (err) {
            console.error('Error processing failed upload:', err, upload);
            toast.error('An upload failed but could not display details');
          }
        });
        
        // Show summary
        if (successCount > 0) {
          toast.success(`${successCount} resume(s) uploaded successfully, ${failedCount} failed`);
        } else {
          toast.error(`All ${failedCount} resume(s) failed to upload`);
        }
      } else {
        toast.success(`All ${successCount} resumes uploaded successfully!`);
      }
      
      setSelectedFiles([]);
      queryClient.invalidateQueries(['resumes']);
    },
    onError: (error) => {
      console.error('Upload error:', error);
      
      // Handle different types of errors gracefully
      let errorMessage = 'Failed to upload resumes';
      
      try {
        if (error?.response?.status === 413 || (error?.message && error.message.includes('413'))) {
          errorMessage = 'Upload size exceeded server limit. Please try uploading in smaller batches (max 50MB total).';
        } else if (error?.response?.status === 500 || (error?.message && error.message.includes('500'))) {
          errorMessage = 'Server error during upload. If files are very large, try uploading fewer files at once.';
        } else if (error?.message) {
          // Safely handle error messages that might be objects
          errorMessage = typeof error.message === 'string' 
            ? error.message 
            : (error.message && typeof error.message === 'object' 
                ? JSON.stringify(error.message) 
                : String(error.message || 'Unknown error'));
        } else if (error && typeof error === 'object') {
          // If error is an object, extract meaningful info
          if (error.status_code && error.timestamp) {
            errorMessage = `Server error (${error.status_code}): ${error.message || 'Upload failed'}`;
          } else {
            errorMessage = error.toString();
          }
        } else if (error) {
          errorMessage = String(error);
        }
      } catch (parseError) {
        console.error('Error parsing upload error:', parseError);
        errorMessage = 'Upload failed with an unparseable error';
      }
      
      toast.error(errorMessage, {
        duration: 8000,
        style: {
          maxWidth: '500px',
        }
      });
    },
  });

  // Enhanced rate resumes mutation with custom weightage - ASYNC VERSION
  const rateResumesMutation = useMutation({
    mutationFn: (data) => apiService.rateResumesWithWeightage(data),
    onSuccess: (response) => {
      // Response now contains job_id instead of immediate results
      toast.success('Rating job started! You can monitor progress.');
      setCurrentRatingJob(response.job_id);
      setShowJobMonitor(true);
      setIsRating(false);
      setShowRatingModal(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to start rating job');
      setIsRating(false);
    },
  });

  // Synchronous rate resumes mutation
  const rateResumesSyncMutation = useMutation({
    mutationFn: (data) => apiService.rateResumesSynchronously(data),
    onSuccess: (response) => {
      console.log('Sync rating completed, response:', response);
      const successful = response?.successful || 0;
      const failed = response?.failed || 0;
      const total = successful + failed;
      
      const successMsg = total > 0 
        ? `Sync rating completed! ${successful}/${total} resumes rated successfully.`
        : 'Sync rating completed!';
      
      toast.success(successMsg, {
        duration: 6000,
        style: {
          background: '#10B981',
          color: 'white',
          fontSize: '16px',
          fontWeight: 'bold'
        }
      });
      
      // Show enhanced browser notification for sync mode
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('⚡ Sync Rating Complete!', {
          body: successMsg,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: 'sync-rating-complete',
          requireInteraction: true
        });
      }
      
      // Force refresh of all related queries
      console.log('Invalidating queries after sync rating, selectedJob:', selectedJob);
      queryClient.invalidateQueries(['resumes']);
      queryClient.invalidateQueries(['resumes', selectedJob]);
      queryClient.invalidateQueries(['rating-jobs']);
      
      // Force immediate refetch to ensure UI updates
      setTimeout(() => {
        console.log('Refetching resumes after sync rating');
        queryClient.refetchQueries(['resumes', selectedJob]);
      }, 500); // Small delay to ensure backend processing is complete
      
      setIsRating(false);
      setShowRatingModal(false);
      setSelectedResumes([]);
    },
    onError: (error) => {
      console.error('Sync rating error:', error);
      toast.error(error.message || 'Failed to rate resumes');
      setIsRating(false);
    },
  });

  // Update resume status mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ resumeId, status }) => apiService.updateResumeStatus(resumeId, status),
    onSuccess: () => {
      toast.success('Status updated successfully!');
      queryClient.invalidateQueries(['resumes']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update status');
    },
  });

  // Shortlisted candidate management mutations
  const sendEmailsMutation = useMutation({
    mutationFn: ({ jobDescriptionId, interviewId, expiryHours }) => 
      apiService.sendShortlistEmails(jobDescriptionId, interviewId, expiryHours),
    onSuccess: (response) => {
      toast.success(`Emails sent! ${response.successful_emails} successful, ${response.failed_emails} failed.`);
      loadShortlistedCandidates();
      queryClient.invalidateQueries(['resumes']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to send emails');
    },
  });

  const resendEmailMutation = useMutation({
    mutationFn: ({ resumeId, interviewId, expiryHours }) => 
      apiService.resendEmail(resumeId, interviewId, expiryHours),
    onSuccess: () => {
      toast.success('Email resent successfully!');
      loadShortlistedCandidates();
      queryClient.invalidateQueries(['resumes']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to resend email');
    },
  });

  const resendAllEmailsMutation = useMutation({
    mutationFn: ({ jobDescriptionId, interviewId, expiryHours }) => 
      apiService.resendAllEmails(jobDescriptionId, interviewId, expiryHours),
    onSuccess: (response) => {
      toast.success(`Emails resent! ${response.successful_emails} successful, ${response.failed_emails} failed.`);
      loadShortlistedCandidates();
      queryClient.invalidateQueries(['resumes']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to resend emails');
    },
  });

  const linkCandidatesMutation = useMutation({
    mutationFn: (jobDescriptionId) => apiService.linkShortlistedCandidates(jobDescriptionId),
    onSuccess: (response) => {
      toast.success(`Candidates linked! ${response.linked_count} candidates linked successfully.`);
      loadShortlistedCandidates();
      queryClient.invalidateQueries(['resumes']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to link candidates');
    },
  });

  const scheduleInterviewMutation = useMutation({
    mutationFn: ({ jobDescriptionId, data }) => apiService.scheduleInterviewForShortlisted(jobDescriptionId, data),
    onSuccess: (response) => {
      toast.success(`Interview scheduled! ${response.total_sessions_created} sessions created.`);
      setShowScheduleModal(false);
      setSelectedInterview('');
      setSelectedCandidatesForScheduling([]);
      queryClient.invalidateQueries(['resumes']);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to schedule interview');
    },
  });

  const generateInterviewLinksMutation = useMutation({
    mutationFn: ({ resumeId, interviewId, expiresInHours }) => 
      apiService.generateInterviewLink(resumeId, interviewId, expiresInHours),
    onSuccess: (response, variables) => {
      toast.success('Interview link generated successfully!');
      setGeneratedLinks(prev => [...prev, { 
        ...response, 
        candidateName: variables.candidateName,
        candidateEmail: variables.candidateEmail 
      }]);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to generate interview link');
    },
  });

  const generateSingleLinkMutation = useMutation({
    mutationFn: ({ resumeId, interviewId, expiresInHours }) => 
      apiService.generateInterviewLink(resumeId, interviewId, expiresInHours),
    onSuccess: (response) => {
      toast.success('Interview link generated successfully!');
      loadCandidateLinks(selectedCandidateForLink?.id);
      // Refresh shortlisted candidates to update active link counts
      loadShortlistedCandidates();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to generate interview link');
    },
  });

  const reopenLinkMutation = useMutation({
    mutationFn: ({ linkId, expiresInHours }) => 
      apiService.reopenInterviewLink(linkId, expiresInHours),
    onSuccess: () => {
      toast.success('Interview link reopened successfully!');
      loadCandidateLinks(selectedCandidateForLink?.id);
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to reopen interview link');
    },
  });

  const deleteLinkMutation = useMutation({
    mutationFn: (linkId) => apiService.deleteInterviewLink(linkId),
    onSuccess: (data, linkId) => {
      toast.success('Interview link deleted successfully!');
      // Remove the deleted link from candidateLinks state immediately
      setCandidateLinks(prev => prev.filter(link => link.id !== linkId));
      // Refresh shortlisted candidates to update active link counts
      loadShortlistedCandidates();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete interview link');
    },
  });

  // Create interview mutation
  const createInterviewMutation = useMutation({
    mutationFn: (data) => apiService.createInterview(data),
    onSuccess: () => {
      toast.success('Interview created successfully!');
      setShowCreateInterviewModal(false);
      // Refresh available interviews to include the new one
      loadAvailableInterviews();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create interview');
    },
  });

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    validateAndSetFiles(files);
  };

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files);
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const filteredFiles = files.filter(file => {
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      return allowedTypes.includes(fileExtension);
    });
    validateAndSetFiles(filteredFiles);
  };

  const validateAndSetFiles = (files) => {
    const maxFileSize = 10 * 1024 * 1024; // 10MB per file
    const maxTotalSize = 50 * 1024 * 1024; // 50MB total (reduced from 100MB due to server limits)
    
    let totalSize = 0;
    const validFiles = [];
    const warnings = [];
    
    for (const file of files) {
      if (file.size > maxFileSize) {
        warnings.push(`${file.name} is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum file size is 10MB.`);
        continue;
      }
      
      totalSize += file.size;
      validFiles.push(file);
    }
    
    if (totalSize > maxTotalSize) {
      toast.error(`Total file size (${(totalSize / 1024 / 1024).toFixed(1)}MB) exceeds ${maxTotalSize / 1024 / 1024}MB limit. Please select fewer files or split into multiple uploads.`, {
        duration: 10000,
        style: {
          maxWidth: '600px',
        }
      });
      return;
    }
    
    if (warnings.length > 0) {
      warnings.forEach(warning => toast.warning(warning, { duration: 6000 }));
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      
      // Show helpful info about selection
      const totalSizeMB = (totalSize / 1024 / 1024).toFixed(1);
      if (validFiles.length !== files.length) {
        toast.info(`Selected ${validFiles.length} valid files (${totalSizeMB}MB) out of ${files.length} total files.`);
      } else {
        toast.success(`Selected ${validFiles.length} files (${totalSizeMB}MB total). Ready to upload!`);
      }
    } else {
      setSelectedFiles([]);
    }
  };

  const handleUpload = async () => {
    if (!selectedJob || selectedFiles.length === 0) {
      toast.error('Please select a job and at least one file');
      return;
    }

    const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    const maxTotalSize = 50 * 1024 * 1024; // 50MB
    
    // Double-check size limit before upload
    if (totalSize > maxTotalSize) {
      toast.error(`Total size (${(totalSize / 1024 / 1024).toFixed(1)}MB) exceeds limit. Please reduce to ${maxTotalSize / 1024 / 1024}MB or less.`);
      return;
    }
    
    console.log(`Starting upload: ${selectedFiles.length} files, total size: ${(totalSize / 1024 / 1024).toFixed(2)}MB`);

    setIsUploading(true);
    const formData = new FormData();
    selectedFiles.forEach(file => {
      formData.append('files', file);
    });
    formData.append('job_description_id', selectedJob);

    try {
      await uploadResumesMutation.mutateAsync(formData);
    } catch (error) {
      console.error('Upload failed in handleUpload:', error);
      // Don't need to show error here since onError handler already does it
    } finally {
      setIsUploading(false);
    }
  };

  // Helper functions for weighted rating system
  const resetWeights = () => {
    setWeights({
      qualification_weight: 1.0,
      yoe_weight: 1.0,
      skill_match_weight: 1.0,
      quality_experience_weight: 1.0,
      industry_fit_weight: 1.0,
      job_stability_weight: 1.0,
      experience_suitability_weight: 1.0
    });
  };

  const updateWeight = (criterion, value) => {
    const numValue = parseFloat(value);
    if (numValue >= 0 && numValue <= 1) {
      setWeights(prev => ({
        ...prev,
        [criterion]: numValue
      }));
    }
  };

  const calculateMaxWeightedScore = () => {
    return Object.values(weights).reduce((sum, weight) => sum + (10 * weight), 0);
  };

  // Helper function to get the correct total score for a resume
  const getCorrectTotalScore = (resume) => {
    // Check if resume has individual criterion scores (new system)
    const hasIndividualScores = resume.qualification_score || resume.yoe_score || 
                               resume.skill_match_score || resume.quality_experience_score ||
                               resume.industry_fit_score || resume.job_stability_score ||
                               resume.experience_suitability_score;
    
    if (hasIndividualScores) {
      // Calculate from weights if available
      const weights = resume.detailed_criteria_scores?.weights;
      if (weights) {
        const calculatedScore = 
          (resume.qualification_score || 0) * (weights.qualification_weight || 1.0) +
          (resume.yoe_score || 0) * (weights.yoe_weight || 1.0) +
          (resume.skill_match_score || 0) * (weights.skill_weight || 1.0) +
          (resume.quality_experience_score || 0) * (weights.qualityofexp_weight || 1.0) +
          (resume.industry_fit_score || 0) * (weights.industryfit_weight || 1.0) +
          (resume.job_stability_score || 0) * (weights.tenure_weight || 1.0) +
          (resume.experience_suitability_score || 0) * (weights.exp_weight || 1.0);
        
        // Debug log for CSV export issues
        if (resume.candidate_name && (resume.candidate_name.includes('Shruti') || resume.candidate_name.includes('Harsh'))) {
          console.log(`Score calculation for ${resume.candidate_name}:`, {
            individual_scores: [resume.qualification_score, resume.yoe_score, resume.skill_match_score, 
                              resume.quality_experience_score, resume.industry_fit_score, 
                              resume.job_stability_score, resume.experience_suitability_score],
            weights: weights,
            calculated: calculatedScore,
            original: resume.score
          });
        }
        
        return Math.round(calculatedScore * 10) / 10; // Round to 1 decimal place
      }
      
      // No weights, sum individual scores
      const totalScore = 
        (resume.qualification_score || 0) +
        (resume.yoe_score || 0) +
        (resume.skill_match_score || 0) +
        (resume.quality_experience_score || 0) +
        (resume.industry_fit_score || 0) +
        (resume.job_stability_score || 0) +
        (resume.experience_suitability_score || 0);
      return Math.round(totalScore * 10) / 10;
    }
    
    // Use the original score for legacy system
    return resume.score;
  };

  // Helper function to determine max possible score for a resume
  const getMaxScoreForResume = (resume) => {
    // Check if resume has individual criterion scores (new system)
    const hasIndividualScores = resume.qualification_score || resume.yoe_score || 
                               resume.skill_match_score || resume.quality_experience_score ||
                               resume.industry_fit_score || resume.job_stability_score ||
                               resume.experience_suitability_score;
    
    if (hasIndividualScores) {
      // Try to get the actual max score from detailed criteria scores
      const maxFromDetailed = resume.detailed_criteria_scores?.max_possible_score;
      if (maxFromDetailed) {
        return Math.round(maxFromDetailed);
      }
      
      // Calculate from weights if available
      const weights = resume.detailed_criteria_scores?.weights;
      if (weights) {
        const totalWeight = (weights.qualification_weight || 1.0) + 
                           (weights.yoe_weight || 1.0) + 
                           (weights.skill_weight || 1.0) + 
                           (weights.qualityofexp_weight || 1.0) + 
                           (weights.industryfit_weight || 1.0) + 
                           (weights.tenure_weight || 1.0) + 
                           (weights.exp_weight || 1.0);
        return Math.round(10 * totalWeight);
      }
      
      // Default fallback for new system with standard weights (all 1.0)
      return 70; // 10 * 7 = 70 (7 criteria, each weighted 1.0, max score 10 each)
    } else {
      // Legacy system or unknown - show 100 for backward compatibility
      return 100;
    }
  };

  const handleBulkRating = () => {
    if (!selectedJob || !filteredResumes?.length) {
      toast.error('No resumes to rate');
      return;
    }

    const resumeIds = selectedResumes.length > 0 
      ? selectedResumes 
      : filteredResumes.map(resume => resume.id);

    const ratingData = {
      resume_ids: resumeIds,
      job_description_id: selectedJob,
      ...(useWeightedRating && { weights: weights })
    };

    setIsRating(true);
    
    if (syncMode) {
      rateResumesSyncMutation.mutate(ratingData);
    } else {
      rateResumesMutation.mutate(ratingData);
    }
  };

  const handleResumeSelection = (resumeId) => {
    // Find the resume to check if text extraction failed
    const resume = filteredResumes.find(r => r.id === resumeId);
    
    // Don't allow selection if text extraction failed
    if (resume && resume.extraction_status && 
        ['failed', 'corrupted', 'empty'].includes(resume.extraction_status)) {
      toast.error('Cannot select resume with failed text extraction');
      return;
    }
    
    setSelectedResumes(prev => 
      prev.includes(resumeId) 
        ? prev.filter(id => id !== resumeId)
        : [...prev, resumeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedResumes.length === filteredResumes.length) {
      setSelectedResumes([]);
    } else {
      // Only select resumes with successful text extraction
      const selectableResumes = filteredResumes.filter(r => 
        !r.extraction_status || 
        !['failed', 'corrupted', 'empty'].includes(r.extraction_status)
      );
      setSelectedResumes(selectableResumes.map(r => r.id) || []);
    }
  };

  const exportToExcel = () => {
    // Filter to include all resumes except pending
    const exportableResumes = filteredResumes.filter(resume => resume.status !== 'pending');
    
    if (!exportableResumes.length) {
      toast.error('No processed resumes to export');
      return;
    }

    // Find the most recent evaluation with weights
    const resumesWithWeights = exportableResumes.filter(r => r.detailed_criteria_scores?.weights);
    const mostRecentWithWeights = resumesWithWeights.length > 0 
      ? resumesWithWeights.reduce((latest, current) => 
          new Date(current.updated_at || current.created_at) > new Date(latest.updated_at || latest.created_at) 
            ? current 
            : latest
        )
      : null;
    
    const headers = mostRecentWithWeights ? [
      'Candidate Name', 'Email', 'Overall Score (Weighted)', 'Status', 'Upload Date',
      `Qualification (weighted: ${mostRecentWithWeights.detailed_criteria_scores.weights.qualification_weight})`,
      `Experience (weighted: ${mostRecentWithWeights.detailed_criteria_scores.weights.yoe_weight})`,
      `Skills (weighted: ${mostRecentWithWeights.detailed_criteria_scores.weights.skill_weight})`,
      `Experience Quality (weighted: ${mostRecentWithWeights.detailed_criteria_scores.weights.qualityofexp_weight})`,
      `Industry Fit (weighted: ${mostRecentWithWeights.detailed_criteria_scores.weights.industryfit_weight})`,
      `Job Stability (weighted: ${mostRecentWithWeights.detailed_criteria_scores.weights.tenure_weight})`,
      `Role Suitability (weighted: ${mostRecentWithWeights.detailed_criteria_scores.weights.exp_weight})`,
      'Rating System', 'Key Strengths', 'Gaps', 'Feedback'
    ] : [
      'Candidate Name', 'Email', 'Overall Score (Weighted)', 'Status', 'Upload Date',
      'Qualification (Weighted)', 'Experience (Weighted)', 'Skills (Weighted)', 
      'Experience Quality (Weighted)', 'Industry Fit (Weighted)', 'Job Stability (Weighted)', 
      'Role Suitability (Weighted)', 'Rating System', 'Key Strengths', 'Gaps', 'Feedback'
    ];

    const csvData = [
      headers,
      ...exportableResumes.map(resume => {
        // Check if resume has individual criterion scores
        const hasIndividualScores = resume.qualification_score || resume.yoe_score || 
                                   resume.skill_match_score || resume.quality_experience_score ||
                                   resume.industry_fit_score || resume.job_stability_score ||
                                   resume.experience_suitability_score;
        
        const correctedScore = getCorrectTotalScore(resume);
        
        // Debug logging for CSV export
        if (resume.candidate_name && resume.candidate_name.includes('Shruti')) {
          console.log('CSV Export - Shruti score:', {
            original: resume.score,
            corrected: correctedScore,
            hasWeights: !!resume.detailed_criteria_scores?.weights
          });
        }
        
        // Calculate weighted individual scores for CSV with denominators
        let weightedScores = {
          qualification: 'N/A',
          experience: 'N/A', 
          skills: 'N/A',
          quality: 'N/A',
          industry: 'N/A',
          stability: 'N/A',
          suitability: 'N/A'
        };
        
        if (hasIndividualScores) {
          const weights = resume.detailed_criteria_scores?.weights;
          if (weights) {
            // Calculate weighted contributions with denominators
            weightedScores = {
              qualification: `${((resume.qualification_score || 0) * (weights.qualification_weight || 1.0)).toFixed(1)}/${(10 * (weights.qualification_weight || 1.0)).toFixed(1)}`,
              experience: `${((resume.yoe_score || 0) * (weights.yoe_weight || 1.0)).toFixed(1)}/${(10 * (weights.yoe_weight || 1.0)).toFixed(1)}`,
              skills: `${((resume.skill_match_score || 0) * (weights.skill_weight || 1.0)).toFixed(1)}/${(10 * (weights.skill_weight || 1.0)).toFixed(1)}`,
              quality: `${((resume.quality_experience_score || 0) * (weights.qualityofexp_weight || 1.0)).toFixed(1)}/${(10 * (weights.qualityofexp_weight || 1.0)).toFixed(1)}`,
              industry: `${((resume.industry_fit_score || 0) * (weights.industryfit_weight || 1.0)).toFixed(1)}/${(10 * (weights.industryfit_weight || 1.0)).toFixed(1)}`,
              stability: `${((resume.job_stability_score || 0) * (weights.tenure_weight || 1.0)).toFixed(1)}/${(10 * (weights.tenure_weight || 1.0)).toFixed(1)}`,
              suitability: `${((resume.experience_suitability_score || 0) * (weights.exp_weight || 1.0)).toFixed(1)}/${(10 * (weights.exp_weight || 1.0)).toFixed(1)}`
            };
          } else {
            // No weights, use raw scores with /10 denominator
            weightedScores = {
              qualification: `${resume.qualification_score || 0}/10`,
              experience: `${resume.yoe_score || 0}/10`,
              skills: `${resume.skill_match_score || 0}/10`,
              quality: `${resume.quality_experience_score || 0}/10`,
              industry: `${resume.industry_fit_score || 0}/10`,
              stability: `${resume.job_stability_score || 0}/10`,
              suitability: `${resume.experience_suitability_score || 0}/10`
            };
          }
        }

        return [
          resume.status === 'pending' ? 'Not Rated' : (resume.candidate_name || 'Unknown'),
          resume.candidate_email || 'Unknown',
          correctedScore ? `${correctedScore}/${getMaxScoreForResume(resume)}` : 'Not Rated',
          resume.status,
          new Date(resume.created_at).toLocaleDateString(),
          // Individual weighted scores
          hasIndividualScores ? weightedScores.qualification : 'Legacy',
          hasIndividualScores ? weightedScores.experience : 'Legacy',
          hasIndividualScores ? weightedScores.skills : 'Legacy',
          hasIndividualScores ? weightedScores.quality : 'Legacy',
          hasIndividualScores ? weightedScores.industry : 'Legacy',
          hasIndividualScores ? weightedScores.stability : 'Legacy',
          hasIndividualScores ? weightedScores.suitability : 'Legacy',
          // Rating system indicator
          hasIndividualScores ? '7-Criteria System' : '2-Criteria Legacy',
          (resume.key_strengths || []).join('; '),
          (resume.gaps || []).join('; '),
          resume.feedback || ''
        ];
      })
    ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `processed_resumes_${selectedJob}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(`${exportableResumes.length} processed resumes exported successfully!`);
  };

  const viewResume = (resume) => {
    setViewingResume(resume);
    setShowViewModal(true);
  };

  const handleDeleteClick = (resume) => {
    setResumeToDelete(resume);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!resumeToDelete) return;
    
    setIsDeleting(true);
    try {
      await apiService.deleteResume(resumeToDelete.id);
      
      // Refresh the resume list
      queryClient.invalidateQueries(['resumes', selectedJob]);
      
      toast.success(`Resume "${resumeToDelete.filename}" deleted successfully`);
      setShowDeleteModal(false);
      setResumeToDelete(null);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.message || 'Failed to delete resume');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setResumeToDelete(null);
  };

  const getScoreColor = (score, resume) => {
    const maxScore = getMaxScoreForResume(resume);
    const percentage = (score / maxScore) * 100;
    
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-gray-100 text-gray-800';
      case 'async_processing': return 'bg-yellow-100 text-yellow-800';
      case 'rated': return 'bg-blue-100 text-blue-800';
      case 'shortlisted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDisplayStatus = (resume) => {
    const asyncStatus = getResumeAsyncStatus(resume);
    if (asyncStatus.isAsync) {
      return { 
        value: 'async_processing',
        label: 'Processing',
        tooltip: `In async rating job (${asyncStatus.activeJobs.length} active)`
      };
    }
    return { 
      value: resume.status, 
      label: resume.status.charAt(0).toUpperCase() + resume.status.slice(1),
      tooltip: null
    };
  };

  // Shortlisted candidate management functions
  const loadShortlistedCandidates = async () => {
    if (!selectedJob) return;
    
    setIsLoadingShortlisted(true);
    try {
      const response = await apiService.getShortlistedCandidates(selectedJob);
      setShortlistedCandidates(response || []);
    } catch (error) {
      toast.error('Failed to load shortlisted candidates');
      console.error('Load shortlisted candidates error:', error);
    } finally {
      setIsLoadingShortlisted(false);
    }
  };

  const openShortlistedModal = async () => {
    setShowShortlistedModal(true);
    await loadShortlistedCandidates();
    await loadAvailableInterviews();
  };

  const loadAvailableInterviews = async () => {
    try {
      const response = await apiService.getInterviews(1, 100);
      const jobInterviews = (response.interviews || []).filter(
        interview => interview.job_description_id === selectedJob
      );
      setAvailableInterviews(jobInterviews);
      
    } catch (error) {
      toast.error('Failed to load interviews');
      console.error('Load interviews error:', error);
    }
  };

  const openScheduleModal = async () => {
    await loadAvailableInterviews();
    setShowScheduleModal(true);
  };

  const filteredShortlistedCandidates = shortlistedCandidates.filter(candidate => {
    if (!shortlistedSearch) return true;
    const searchTerm = shortlistedSearch.toLowerCase();
    return (
      candidate.candidate_name?.toLowerCase().includes(searchTerm) ||
      candidate.candidate_email?.toLowerCase().includes(searchTerm)
    );
  });

  console.log('Shortlisted candidates state:', shortlistedCandidates);
  console.log('Filtered shortlisted candidates:', filteredShortlistedCandidates);

  // Load candidate links when modal opens
  useEffect(() => {
    if (showSingleLinkModal && selectedCandidateForLink) {
      loadCandidateLinks(selectedCandidateForLink.id);
      loadAvailableInterviews(); // Also load available interviews
    }
  }, [showSingleLinkModal, selectedCandidateForLink]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update candidates needing links count when interview selection or candidates change
  useEffect(() => {
    // Debounce the API calls to prevent excessive requests
    const timeoutId = setTimeout(async () => {
      if (selectedInterviewForLinks && shortlistedCandidates.length > 0 && !isLoadingCandidatesNeeding) {
        setIsLoadingCandidatesNeeding(true);
        try {
          const candidatesNeedingLinksData = await getCandidatesNeedingLinks();
          setCandidatesNeedingLinksCount(candidatesNeedingLinksData.length);
          setCandidatesNeedingLinks(candidatesNeedingLinksData);
        } catch (error) {
          console.error('Error updating candidates needing links count:', error);
          setCandidatesNeedingLinksCount(filteredShortlistedCandidates.length);
          setCandidatesNeedingLinks(filteredShortlistedCandidates);
        } finally {
          setIsLoadingCandidatesNeeding(false);
        }
      } else if (!selectedInterviewForLinks) {
        setCandidatesNeedingLinksCount(filteredShortlistedCandidates.length);
        setCandidatesNeedingLinks(filteredShortlistedCandidates);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [selectedInterviewForLinks, shortlistedCandidates, shortlistedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendEmails = (interviewId = null, expiryHours = 24) => {
    if (!selectedJob) {
      toast.error('Please select a job first');
      return;
    }
    setIsEmailOperationLoading(true);
    sendEmailsMutation.mutate({ 
      jobDescriptionId: selectedJob, 
      interviewId, 
      expiryHours 
    });
  };

  const handleResendEmail = (resumeId, interviewId = null, expiryHours = 24) => {
    resendEmailMutation.mutate({ 
      resumeId, 
      interviewId, 
      expiryHours 
    });
  };

  const handleResendAllEmails = (interviewId = null, expiryHours = 24) => {
    if (!selectedJob) {
      toast.error('Please select a job first');
      return;
    }
    setIsEmailOperationLoading(true);
    resendAllEmailsMutation.mutate({ 
      jobDescriptionId: selectedJob, 
      interviewId, 
      expiryHours 
    });
  };

  const handleLinkCandidates = () => {
    if (!selectedJob) {
      toast.error('Please select a job first');
      return;
    }
    linkCandidatesMutation.mutate(selectedJob);
  };

  const handleScheduleInterview = () => {
    if (!selectedInterview) {
      toast.error('Please select an interview');
      return;
    }

    const data = {
      interview_id: selectedInterview,
      candidate_ids: selectedCandidatesForScheduling.length > 0 ? selectedCandidatesForScheduling : undefined
    };

    scheduleInterviewMutation.mutate({
      jobDescriptionId: selectedJob,
      data
    });
  };

  // Helper function to get candidates who need links for the selected interview
  const getCandidatesNeedingLinks = async () => {
    if (!selectedInterviewForLinks || !selectedJob) {
      return filteredShortlistedCandidates;
    }
    
    const cacheKey = `${selectedJob}-${selectedInterviewForLinks}`;
    
    try {
      let existingLinks;
      
      // Check cache first
      if (interviewLinksCache[cacheKey] && Date.now() - interviewLinksCache[cacheKey].timestamp < 30000) {
        // Use cached data if less than 30 seconds old
        existingLinks = interviewLinksCache[cacheKey].data;
      } else {
        // Get existing interview links for this job and interview
        const response = await apiService.getJobInterviewLinks(selectedJob, 1, 1000);
        existingLinks = response;
        
        // Cache the response
        setInterviewLinksCache(prev => ({
          ...prev,
          [cacheKey]: {
            data: response,
            timestamp: Date.now()
          }
        }));
      }
      
      // Filter existing active links for the selected interview
      const existingActiveLinksForInterview = existingLinks.links?.filter(link => 
        link.interview?.id === selectedInterviewForLinks && link.status === 'active'
      ) || [];
      
      // Get resume IDs that already have active links for this interview
      const resumeIdsWithActiveLinks = new Set(
        existingActiveLinksForInterview.map(link => link.resume_id)
      );

      // Filter candidates who don't already have active links for this interview
      const candidatesNeedingLinks = filteredShortlistedCandidates.filter(
        candidate => !resumeIdsWithActiveLinks.has(candidate.id)
      );
      
      return candidatesNeedingLinks;
    } catch (error) {
      console.error('Error checking existing links:', error);
      // Fallback: if API fails, return all candidates
      return filteredShortlistedCandidates;
    }
  };

  const handleGenerateInterviewLinks = async () => {
    if (!selectedInterviewForLinks) {
      toast.error('Please select an interview');
      return;
    }

    setIsGeneratingLinks(true);
    setGeneratedLinks([]);

    try {
      // Get candidates who need links for this interview
      const candidatesNeedingLinks = await getCandidatesNeedingLinks();

      if (candidatesNeedingLinks.length === 0) {
        toast.info('All candidates already have active links for this interview');
        return;
      }

      // Generate links only for candidates who don't have active links
      const linkPromises = candidatesNeedingLinks.map(candidate => {
        return generateInterviewLinksMutation.mutateAsync({
          resumeId: candidate.id,
          interviewId: selectedInterviewForLinks,
          expiresInHours: linkExpiryHours,
          candidateName: candidate.candidate_name,
          candidateEmail: candidate.candidate_email
        });
      });

      await Promise.all(linkPromises);
      
      const skippedCount = filteredShortlistedCandidates.length - candidatesNeedingLinks.length;
      let message = `Generated ${candidatesNeedingLinks.length} interview links!`;
      if (skippedCount > 0) {
        message += ` (${skippedCount} candidates already had active links)`;
      }
      toast.success(message);
      
      // Refresh shortlisted candidates to update active link counts
      loadShortlistedCandidates();
    } catch (error) {
      console.error('Error generating interview links:', error);
      toast.error('Some links failed to generate');
    } finally {
      setIsGeneratingLinks(false);
    }
  };

  const loadCandidateLinks = async (resumeId) => {
    if (!resumeId) return;
    
    try {
      const links = await apiService.getResumeInterviewLinks(resumeId);
      setCandidateLinks(links);
    } catch (error) {
      console.error('Failed to load candidate links:', error);
      setCandidateLinks([]);
    }
  };

  const handleGenerateSingleLink = () => {
    if (!singleLinkInterview || !selectedCandidateForLink) {
      toast.error('Please select an interview');
      return;
    }

    generateSingleLinkMutation.mutate({
      resumeId: selectedCandidateForLink.id,
      interviewId: singleLinkInterview,
      expiresInHours: singleLinkExpiry
    });
  };

  const handleReopenLink = (linkId) => {
    reopenLinkMutation.mutate({
      linkId,
      expiresInHours: 24
    });
  };

  const handleDeleteLink = (linkId) => {
    if (window.confirm('Are you sure you want to delete this interview link? This action cannot be undone.')) {
      deleteLinkMutation.mutate(linkId);
    }
  };

  const getEmailStatusIcon = (resume) => {
    if (!resume.email_sent) {
      return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" title="Email not sent" />;
    }
    
    if (resume.email_status === 'sent') {
      return <CheckCircleIcon className="h-4 w-4 text-green-500" title="Email sent successfully" />;
    }
    
    if (resume.email_status === 'failed') {
      return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" title="Email failed to send" />;
    }
    
    return null;
  };

  const getCandidateLoginStatus = (resume) => {
    if (resume.candidate_id) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          Joined
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
        Not Joined
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Resume Management</h1>
        <p className="text-gray-600">Upload, rate, and manage candidate resumes with advanced analytics.</p>
      </div>

      {/* Job Selection */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Select Job Description</h3>
        <select
          value={selectedJob}
          onChange={(e) => {
            const newValue = e.target.value;
            setSelectedJob(newValue);
            // Save to localStorage
            if (newValue) {
              localStorage.setItem('selectedJobId', newValue);
            } else {
              // Clear from localStorage when no JD is selected
              localStorage.removeItem('selectedJobId');
            }
          }}
          className="input-field max-w-md"
        >
          <option value="">Select a job description...</option>
          {jobsData?.job_descriptions?.map((job) => (
            <option key={job.id} value={job.id}>
              {job.title} - {job.company}
            </option>
          ))}
        </select>
      </div>

      {/* Enhanced File Upload */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Resumes</h3>
        
        {!selectedJob ? (
          <div className="border-2 border-dashed border-gray-200 rounded-lg p-8">
            <div className="text-center">
              <CloudArrowUpIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">Please select a job description first</p>
              <p className="text-gray-400 text-sm">Choose a job description from the dropdown above to enable resume upload</p>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
            <div className="text-center">
              <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              
              <div className="flex justify-center space-x-4 mb-4">
                <label className="cursor-pointer bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500 flex items-center space-x-2">
                  <DocumentIcon className="h-5 w-5" />
                  <span>Select Files</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="sr-only"
                  />
                </label>
                
                <label className="cursor-pointer bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500 flex items-center space-x-2">
                  <FolderIcon className="h-5 w-5" />
                  <span>Select Folder</span>
                  <input
                    ref={folderInputRef}
                    type="file"
                    webkitdirectory=""
                    onChange={handleFolderSelect}
                    className="sr-only"
                  />
                </label>
              </div>
              
              <p className="text-xs text-gray-500">PDF, DOC, DOCX up to 10MB each</p>
            </div>
            
            {selectedFiles.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Selected files ({selectedFiles.length}):</p>
                <div className="max-h-32 overflow-y-auto">
                  <ul className="text-sm text-gray-900 space-y-1">
                    {selectedFiles.slice(0, 10).map((file, index) => (
                      <li key={index} className="flex items-center">
                        <DocumentIcon className="h-4 w-4 text-gray-400 mr-2" />
                        {file.name}
                      </li>
                    ))}
                    {selectedFiles.length > 10 && (
                      <li className="text-gray-500">... and {selectedFiles.length - 10} more files</li>
                    )}
                  </ul>
                </div>
                
                <div className="mt-4 flex space-x-3">
                  <button
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="btn-primary disabled:opacity-50"
                  >
                    {isUploading ? 'Uploading...' : `Upload ${selectedFiles.length} Resume(s)`}
                  </button>
                  <button
                    onClick={() => setSelectedFiles([])}
                    className="btn-secondary"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Enhanced Resume Management */}
      {selectedJob && (
        <div className="card">
          {/* Header with responsive layout */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">
                Resumes ({filteredResumes.length})
              </h3>
              <div className="flex items-center space-x-2">
                {/* View Mode Toggle */}
                <div className="flex border border-gray-300 rounded-lg">
                  <button
                    onClick={() => setViewMode('card')}
                    className={`px-2 py-1 text-sm ${viewMode === 'card' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'} rounded-l-lg`}
                    title="Card View"
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-2 py-1 text-sm ${viewMode === 'table' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-50'} rounded-r-lg`}
                    title="Table View"
                  >
                    <TableCellsIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Filters Row */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {/* Status Filter */}
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input-field text-sm"
                style={{minWidth: '140px'}}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="async_processing">Processing</option>
                <option value="rated">Rated</option>
                <option value="shortlisted">Shortlisted</option>
                <option value="rejected">Rejected</option>
                <option value="extraction_failed">Extraction Failed</option>
              </select>

              {/* Sort by Criterion */}
              <select
                value={sortByCriterion}
                onChange={(e) => setSortByCriterion(e.target.value)}
                className="input-field text-sm"
                style={{minWidth: '160px'}}
                title="Sort candidates by specific criteria scores"
              >
                <option value="">Sort by Overall Score</option>
                <option value="qualification_score">Sort by Qualification</option>
                <option value="yoe_score">Sort by Experience</option>
                <option value="skill_match_score">Sort by Skills</option>
                <option value="quality_experience_score">Sort by Quality</option>
                <option value="industry_fit_score">Sort by Industry</option>
                <option value="job_stability_score">Sort by Stability</option>
                <option value="experience_suitability_score">Sort by Suitability</option>
              </select>
            </div>
            
            {/* Action Buttons Row - Aligned to Right */}
            <div className="flex flex-wrap items-center gap-2 justify-end">
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                className="btn-secondary flex items-center space-x-1 px-3 py-2 text-sm"
                title="Refresh data"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span>Refresh</span>
              </button>
              {/* Rating Configuration */}
              {filteredResumes.length > 0 && (
                <button
                  onClick={() => setShowRatingModal(true)}
                  disabled={rateResumesMutation.isLoading || isRating || selectedResumes.length === 0}
                  className="btn-primary disabled:opacity-50 flex items-center space-x-1 px-3 py-2 text-sm"
                  title={selectedResumes.length === 0 ? "Please select resumes to configure rating" : "Configure rating parameters"}
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                  <span>{isRating ? 'Rating...' : 'Evaluate Resumes'}</span>
                </button>
              )}
              
              {/* Export Button - Now Blue */}
              <button
                onClick={exportToExcel}
                className="btn-primary flex items-center space-x-1 px-3 py-2 text-sm"
                disabled={!filteredResumes.filter(r => r.status !== 'pending').length}
                title="Export processed resumes to Excel"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span>Export Evaluation</span>
              </button>
              

              {/* Rating Jobs History - Now Blue */}
              <button
                onClick={() => setShowJobsList(true)}
                className="btn-primary flex items-center space-x-1 px-3 py-2 text-sm"
                title="View rating job history"
              >
                <TableCellsIcon className="h-4 w-4" />
                <span>Evaluation history</span>
              </button>

              {/* Schedule Interview - Only Enabled if Shortlisted Candidates */}
              {selectedJob && (
                <button
                  onClick={openShortlistedModal}
                  className="btn-primary flex items-center space-x-1 px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!filteredResumes.some(r => r.status === 'shortlisted')}
                  title={filteredResumes.some(r => r.status === 'shortlisted') ? "Schedule interviews for shortlisted candidates" : "No shortlisted candidates available"}
                >
                  <UserGroupIcon className="h-4 w-4" />
                  <span>Schedule Interview</span>
                </button>
              )}
            </div>
          </div>

          {/* Bulk Selection Controls */}
          {filteredResumes.length > 0 && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedResumes.length === filteredResumes.length}
                    onChange={handleSelectAll}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-600">
                    {selectedResumes.length > 0 ? `${selectedResumes.length} selected` : 'Select all'}
                  </span>
                </label>
              </div>
              {selectedResumes.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">{selectedResumes.length} resumes selected</span>
                  <button
                    onClick={() => setSelectedResumes([])}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Resume Display */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredResumes.length > 0 ? (
            viewMode === 'table' ? (
              // Table View
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedResumes.length === filteredResumes.length}
                          onChange={handleSelectAll}
                          className="rounded"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        S.No.
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Candidate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Key Strengths
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Gaps
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Uploaded
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredResumes.map((resume, index) => {
                      const isExtractionFailed = resume.extraction_status && 
                        ['failed', 'corrupted', 'empty'].includes(resume.extraction_status);
                      
                      return (
                        <tr key={resume.id} 
                            className={`hover:bg-gray-50 ${isExtractionFailed ? 'opacity-40 bg-gray-50' : ''}`}>
                          <td className="px-3 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedResumes.includes(resume.id)}
                              onChange={() => handleResumeSelection(resume.id)}
                              disabled={isExtractionFailed}
                              className={`rounded ${isExtractionFailed ? 'cursor-not-allowed' : ''}`}
                            />
                          </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <DocumentIcon className="h-8 w-8 text-gray-400 mr-3" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {resume.status === 'pending' ? 'Not Rated' : (resume.candidate_name || 'Unknown')}
                              </div>
                              <div className="text-sm text-gray-500">{resume.filename}</div>
                              {resume.extraction_status && resume.extraction_status !== 'success' && (
                                <div className="flex items-center mt-1">
                                  <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-1" />
                                  <span className="text-xs text-yellow-600">
                                    {resume.extraction_status === 'failed' && 'Text extraction failed'}
                                    {resume.extraction_status === 'empty' && 'No text content found'}
                                    {resume.extraction_status === 'corrupted' && 'File appears corrupted'}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {resume.score ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getScoreColor(getCorrectTotalScore(resume), resume)}`}>
                              <StarIcon className="h-3 w-3 mr-1" />
                              {getCorrectTotalScore(resume)}/{getMaxScoreForResume(resume)}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">Not rated</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const displayStatus = getDisplayStatus(resume);
                            return displayStatus.value === 'async_processing' ? (
                              <span 
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(displayStatus.value)}`}
                                title={displayStatus.tooltip}
                              >
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-1"></div>
                                {displayStatus.label}
                              </span>
                            ) : (
                              <select
                                value={resume.status}
                                onChange={(e) => updateStatusMutation.mutate({
                                  resumeId: resume.id,
                                  status: e.target.value
                                })}
                                className={`text-xs font-medium rounded-full px-2.5 py-0.5 border-0 ${getStatusColor(resume.status)}`}
                              >
                                <option value="pending">Pending</option>
                                <option value="rated">Rated</option>
                                <option value="shortlisted">Shortlisted</option>
                                <option value="rejected">Rejected</option>
                              </select>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 max-w-xs truncate">
                            {resume.key_strengths?.slice(0, 2).join(', ') || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 max-w-xs truncate">
                            {resume.gaps?.slice(0, 2).join(', ') || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(resume.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {resume.candidate_email || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button 
                              onClick={() => viewResume(resume)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteClick(resume)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete resume"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              // Card View
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResumes.map((resume) => {
                  const isExtractionFailed = resume.extraction_status && 
                    ['failed', 'corrupted', 'empty'].includes(resume.extraction_status);
                  
                  return (
                    <div key={resume.id} 
                         className={`bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow ${isExtractionFailed ? 'opacity-40 bg-gray-50' : ''}`}>
                      <div className="flex items-start justify-between mb-4">
                        <input
                          type="checkbox"
                          checked={selectedResumes.includes(resume.id)}
                          onChange={() => handleResumeSelection(resume.id)}
                          disabled={isExtractionFailed}
                          className={`rounded ${isExtractionFailed ? 'cursor-not-allowed' : ''}`}
                        />
                      <div className="flex space-x-2">
                        <button
                          onClick={() => viewResume(resume)}
                          className="text-primary-600 hover:text-primary-900"
                          title="View resume"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(resume)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete resume"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 text-lg mb-1">
                        {resume.status === 'pending' ? 'Not Rated' : (resume.candidate_name || 'Unknown Candidate')}
                      </h4>
                      <p className="text-sm text-gray-500 mb-2">Email: {resume.candidate_email || 'N/A'}</p>
                      <p className="text-sm text-gray-500 mb-2">{resume.filename}</p>
                      <p className="text-xs text-gray-400">
                        Uploaded: {new Date(resume.created_at).toLocaleDateString()}
                      </p>
                      {resume.extraction_status && resume.extraction_status !== 'success' && (
                        <div className="flex items-center mt-2">
                          <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-1" />
                          <span className="text-xs text-yellow-600">
                            {resume.extraction_status === 'failed' && 'Text extraction failed'}
                            {resume.extraction_status === 'empty' && 'No text content found'}
                            {resume.extraction_status === 'corrupted' && 'File appears corrupted'}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        {resume.score ? (
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(getCorrectTotalScore(resume), resume)}`}>
                            ⭐ {getCorrectTotalScore(resume)}/{getMaxScoreForResume(resume)}
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm">Not rated</span>
                        )}
                        {/* Rating System Indicator */}
                        {resume.score && (() => {
                          const hasIndividualScores = resume.qualification_score || resume.yoe_score || 
                                                     resume.skill_match_score || resume.quality_experience_score ||
                                                     resume.industry_fit_score || resume.job_stability_score ||
                                                     resume.experience_suitability_score;
                          return hasIndividualScores ? (
                            <span 
                              className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full font-medium"
                              title="Rated with Enhanced 7-Criteria System"
                            >
                              7-Criteria
                            </span>
                          ) : (
                            <span 
                              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                              title="Rated with Legacy 2-Criteria System"
                            >
                              Legacy
                            </span>
                          );
                        })()}
                      </div>
                      {(() => {
                        const displayStatus = getDisplayStatus(resume);
                        return displayStatus.value === 'async_processing' ? (
                          <span 
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${getStatusColor(displayStatus.value)}`}
                            title={displayStatus.tooltip}
                          >
                            <div className="animate-spin rounded-full h-2 w-2 border-b-2 border-current mr-1"></div>
                            {displayStatus.label}
                          </span>
                        ) : (
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(resume.status)}`}>
                            {displayStatus.label}
                          </span>
                        );
                      })()}
                    </div>

                    {resume.score && (
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">Key Strengths:</p>
                          <div className="flex flex-wrap gap-1">
                            {(resume.key_strengths || []).slice(0, 2).map((strength, idx) => (
                              <span key={idx} className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                                {strength}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">Areas for Improvement:</p>
                          <div className="flex flex-wrap gap-1">
                            {(resume.gaps || []).slice(0, 2).map((gap, idx) => (
                              <span key={idx} className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">
                                {gap}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )
          ) : (
            <div className="text-center py-8">
              <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">
                {filterStatus === 'async_processing' 
                  ? 'No resumes currently being processed'
                  : filterStatus === 'pending'
                    ? 'No pending (unrated) resumes found'
                    : filterStatus 
                      ? `No ${filterStatus} resumes found`
                      : 'No resumes found for this job'
                }
              </p>
              <p className="text-sm text-gray-400">
                {filterStatus ? 'Try selecting a different status filter' : 'Upload some resumes to get started'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Rating Configuration Modal */}
      {showRatingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-6 pb-0">
              <h3 className="text-lg font-semibold">Resume Rating System</h3>
              <button
                onClick={() => setShowRatingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4 p-6 overflow-y-auto flex-1">
              {/* New Scoring System Explanation */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">New Scoring System</h4>
                <p className="text-sm text-blue-800 mb-3">
                  The enhanced rating system evaluates resumes using 7 criteria, each rated on a scale of 1-10:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-blue-700">
                  <div>• Qualification Assessment (1-10)</div>
                  <div>• Years of Experience (1-10)</div>
                  <div>• Skills Match (1-10)</div>
                  <div>• Quality of Experience (1-10)</div>
                  <div>• Industry Fit (1-10)</div>
                  <div>• Job Stability (1-10)</div>
                  <div>• Experience Suitability (1-10)</div>
                </div>
                <p className="text-sm text-blue-800 mt-3">
                  <strong>Total Score:</strong> Simple sum of all 7 ratings (Maximum: 70 points)
                </p>
              </div>

              {/* Advanced Weighting Section */}
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-green-900">Advanced Weighting System</h4>
                  <button
                    onClick={() => setUseWeightedRating(!useWeightedRating)}
                    className="text-xs bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded"
                  >
                    {useWeightedRating ? 'Hide' : 'Show'} Weighting
                  </button>
                </div>
                
                {useWeightedRating && (
                  <div className="space-y-3">
                    <p className="text-sm text-green-800 mb-3">
                      Customize the importance of each criterion (0.0 = ignore, 1.0 = full weight).
                      <br />
                      <strong>Current Max Score: {calculateMaxWeightedScore().toFixed(1)} points</strong>
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {[
                        { key: 'qualification_weight', label: 'Qualification Assessment' },
                        { key: 'yoe_weight', label: 'Years of Experience' },
                        { key: 'skill_match_weight', label: 'Skills Match' },
                        { key: 'quality_experience_weight', label: 'Quality of Experience' },
                        { key: 'industry_fit_weight', label: 'Industry Fit' },
                        { key: 'job_stability_weight', label: 'Job Stability' },
                        { key: 'experience_suitability_weight', label: 'Experience Suitability' }
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center space-x-2">
                          <label className="text-xs text-green-700 flex-1">{label}:</label>
                          <input
                            type="number"
                            min="0"
                            max="1"
                            step="0.1"
                            value={weights[key]}
                            onChange={(e) => updateWeight(key, e.target.value)}
                            className="w-16 px-2 py-1 text-xs border border-green-300 rounded focus:ring-green-500 focus:border-green-500"
                          />
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex space-x-2 mt-3">
                      <button
                        onClick={resetWeights}
                        className="text-xs bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded"
                      >
                        Reset All to 1.0
                      </button>
                      <button
                        onClick={() => setWeights(prev => Object.fromEntries(
                          Object.keys(prev).map(key => [key, 0.5])
                        ))}
                        className="text-xs bg-green-100 hover:bg-green-200 text-green-800 px-2 py-1 rounded"
                      >
                        Set All to 0.5
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Sync Mode Toggle */}
              <div className="bg-yellow-50 p-3 rounded-lg">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="syncMode"
                    checked={syncMode}
                    onChange={(e) => setSyncMode(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="syncMode" className="text-sm font-medium text-gray-700">
                    Synchronous Mode (Process immediately, may be slower)
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {syncMode 
                    ? "Resumes will be processed immediately. This may take longer for large batches." 
                    : "Resumes will be processed in background. You can monitor progress via the job status."}
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 pt-0">
              <button
                onClick={() => setShowRatingModal(false)}
                className="btn-secondary"
                disabled={isRating}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkRating}
                disabled={isRating}
                className="btn-primary disabled:opacity-50"
              >
                {isRating 
                  ? (syncMode ? 'Processing...' : 'Starting Job...') 
                  : (syncMode ? 'Rate Now' : 'Start Async Rating')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume View Modal */}
      {showViewModal && viewingResume && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">
                {viewingResume.status === 'pending' ? 'Resume Details (Not Rated)' : (viewingResume.candidate_name || 'Resume Details')}
              </h3>
              {/* <h2 className="text-l font-semibold">Email: {viewingResume.candidate_emails || 'Resume Details'}</h2> */}
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Resume Information</h4>
                <div className="space-y-2 text-sm">
                  <p><span className="font-medium">File:</span> {viewingResume.filename}</p>
                  <p><span className="font-medium">Uploaded:</span> {new Date(viewingResume.created_at).toLocaleDateString()}</p>
                  <p><span className="font-medium">Email:</span> {viewingResume.candidate_email || 'N/A'}</p>
                  <p><span className="font-medium">Status:</span> 
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(viewingResume.status)}`}>
                      {viewingResume.status}
                    </span>
                  </p>
                  {viewingResume.extraction_status && viewingResume.extraction_status !== 'success' && (
                    <p><span className="font-medium">Extraction Status:</span> 
                      <span className="ml-2 inline-flex items-center text-yellow-600">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {viewingResume.extraction_status === 'failed' && 'Text extraction failed'}
                        {viewingResume.extraction_status === 'empty' && 'No text content found'}
                        {viewingResume.extraction_status === 'corrupted' && 'File appears corrupted'}
                      </span>
                    </p>
                  )}
                  {viewingResume.score && (
                    <p><span className="font-medium">Score:</span> 
                      <span className={`ml-2 px-2 py-1 rounded-full text-sm font-medium ${getScoreColor(getCorrectTotalScore(viewingResume), viewingResume)}`}>
                        {getCorrectTotalScore(viewingResume)}/{getMaxScoreForResume(viewingResume)}
                      </span>
                    </p>
                  )}
                </div>
              </div>

              {viewingResume.score && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Rating Details</h4>
                  <div className="space-y-3">
                    {viewingResume.feedback && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-1">AI Feedback:</p>
                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{viewingResume.feedback}</p>
                      </div>
                    )}
                    
                    {viewingResume.key_strengths?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Key Strengths:</p>
                        <div className="flex flex-wrap gap-2">
                          {viewingResume.key_strengths.map((strength, idx) => (
                            <span key={idx} className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full">
                              {strength}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {viewingResume.gaps?.length > 0 && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Areas for Improvement:</p>
                        <div className="flex flex-wrap gap-2">
                          {viewingResume.gaps.map((gap, idx) => (
                            <span key={idx} className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full">
                              {gap}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Individual Criterion Scores */}
              {viewingResume.score && (
                <div className="col-span-1 md:col-span-2 mt-4">
                  <h4 className="font-medium text-gray-900 mb-3">Detailed Criterion Scores</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    {(() => {
                      // Check if this resume has individual criterion scores (new system)
                      const hasIndividualScores = viewingResume.qualification_score || 
                                                viewingResume.yoe_score || 
                                                viewingResume.skill_match_score ||
                                                viewingResume.quality_experience_score ||
                                                viewingResume.industry_fit_score ||
                                                viewingResume.job_stability_score ||
                                                viewingResume.experience_suitability_score;
                      
                      if (hasIndividualScores) {
                        // New 7-criteria system
                        const criteria = [
                          { name: 'Qualification', score: viewingResume.qualification_score, color: 'blue' },
                          { name: 'Years of Experience', score: viewingResume.yoe_score, color: 'green' },
                          { name: 'Skills Match', score: viewingResume.skill_match_score, color: 'purple' },
                          { name: 'Quality of Experience', score: viewingResume.quality_experience_score, color: 'indigo' },
                          { name: 'Industry Fit', score: viewingResume.industry_fit_score, color: 'yellow' },
                          { name: 'Job Stability', score: viewingResume.job_stability_score, color: 'red' },
                          { name: 'Experience Suitability', score: viewingResume.experience_suitability_score, color: 'pink' }
                        ];
                        
                        // Check if weights were applied
                        const appliedWeights = viewingResume.detailed_criteria_scores?.weights;
                        const hasCustomWeights = appliedWeights && Object.values(appliedWeights).some(w => w !== 1.0);
                        
                        return (
                          <div>
                            <p className="text-sm text-gray-600 mb-3">
                              Rated using Enhanced 7-Criteria System (1-10 scale each{hasCustomWeights ? ', weighted' : ', total score is sum of all ratings'}):
                            </p>
                            
                            {/* Show weights if custom weights were applied */}
                            {hasCustomWeights && appliedWeights && (
                              <div className="mb-4 p-3 bg-blue-50 rounded border-l-4 border-blue-400">
                                <p className="text-sm font-medium text-blue-800 mb-2">Applied Weights:</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-blue-700">
                                  <span>Qualification: {appliedWeights.qualification_weight || 1.0}</span>
                                  <span>Experience: {appliedWeights.yoe_weight || 1.0}</span>
                                  <span>Skills: {appliedWeights.skill_weight || 1.0}</span>
                                  <span>Quality: {appliedWeights.qualityofexp_weight || 1.0}</span>
                                  <span>Industry: {appliedWeights.industryfit_weight || 1.0}</span>
                                  <span>Stability: {appliedWeights.tenure_weight || 1.0}</span>
                                  <span>Suitability: {appliedWeights.exp_weight || 1.0}</span>
                                </div>
                              </div>
                            )}
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {criteria.map((criterion, idx) => {
                                const weightKeys = ['qualification_weight', 'yoe_weight', 'skill_weight', 'qualityofexp_weight', 'industryfit_weight', 'tenure_weight', 'exp_weight'];
                                const weight = appliedWeights ? appliedWeights[weightKeys[idx]] || 1.0 : 1.0;
                                const rawScore = criterion.score || 0;
                                const weightedContribution = rawScore * weight;
                                
                                return (
                                  <div key={idx} className="bg-white p-3 rounded border">
                                    <div className="flex justify-between items-center mb-1">
                                      <span className="text-sm font-medium text-gray-700">{criterion.name}:</span>
                                      {hasCustomWeights ? (
                                        <div className="text-right">
                                          <span className={`px-2 py-1 rounded text-sm font-bold ${
                                            weightedContribution >= (weight * 8) ? 'bg-green-100 text-green-800' :
                                            weightedContribution >= (weight * 6) ? 'bg-yellow-100 text-yellow-800' :
                                            weightedContribution >= (weight * 4) ? 'bg-orange-100 text-orange-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                            {weightedContribution.toFixed(1)}/{(weight * 10).toFixed(1)}
                                          </span>
                                          <div className="text-xs text-gray-500 mt-1">
                                            Raw: {rawScore}/10 × {weight} = {weightedContribution.toFixed(1)}
                                          </div>
                                        </div>
                                      ) : (
                                        <span className={`px-2 py-1 rounded text-sm font-bold ${
                                          criterion.score >= 8 ? 'bg-green-100 text-green-800' :
                                          criterion.score >= 6 ? 'bg-yellow-100 text-yellow-800' :
                                          criterion.score >= 4 ? 'bg-orange-100 text-orange-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          {rawScore}/10
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      } else {
                        // Legacy 2-criteria system
                        return (
                          <div className="text-center py-4">
                            <p className="text-sm text-gray-500">
                              This resume was rated using the legacy 2-criteria system.
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Re-rate using the new system to see detailed criterion scores.
                            </p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}
            </div>

            {viewingResume.extracted_text && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">Extracted Content Preview</h4>
                  <span className="text-xs text-gray-500">
                    {viewingResume.extracted_text.length.toLocaleString()} characters
                  </span>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg max-h-96 overflow-y-auto border">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {viewingResume.extracted_text}
                  </p>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Scroll to view all content from all pages
                </p>
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowViewModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
              {!viewingResume.score && (
                <button
                  onClick={() => {
                    // Check if text extraction failed
                    if (viewingResume.extraction_status && 
                        ['failed', 'corrupted', 'empty'].includes(viewingResume.extraction_status)) {
                      toast.error('Cannot rate resume with failed text extraction');
                      return;
                    }
                    setSelectedResumes([viewingResume.id]);
                    setShowViewModal(false);
                    setShowRatingModal(true);
                  }}
                  disabled={viewingResume.extraction_status && 
                    ['failed', 'corrupted', 'empty'].includes(viewingResume.extraction_status)}
                  className={`btn-primary ${
                    viewingResume.extraction_status && 
                    ['failed', 'corrupted', 'empty'].includes(viewingResume.extraction_status)
                      ? 'opacity-50 cursor-not-allowed' 
                      : ''
                  }`}
                  title={
                    viewingResume.extraction_status && 
                    ['failed', 'corrupted', 'empty'].includes(viewingResume.extraction_status)
                      ? 'Cannot rate resume with failed text extraction'
                      : 'Rate this resume'
                  }
                >
                  Rate This Resume
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rating Job Monitor */}
      {showJobMonitor && currentRatingJob && (
        <RatingJobMonitor
          jobId={currentRatingJob}
          onComplete={(results) => {
            // Rating job completed successfully
            console.log('Async rating completed, results:', results);
            const successMsg = `Rating completed! ${results.successful}/${results.total_processed} resumes rated.`;
            toast.success(successMsg, {
              duration: 6000, // Show for 6 seconds
              style: {
                background: '#10B981',
                color: 'white',
                fontSize: '16px',
                fontWeight: 'bold'
              }
            });
            
            // Show enhanced browser notification
            if ('Notification' in window && Notification.permission === 'granted') {
              new Notification('🎉 Resume Rating Complete!', {
                body: successMsg,
                icon: '/favicon.ico',
                badge: '/favicon.ico',
                tag: 'rating-complete', // Prevent duplicate notifications
                requireInteraction: true // Keep notification visible until user interacts
              });
            }
            
            // Force refresh of all related queries
            queryClient.invalidateQueries(['resumes']);
            queryClient.invalidateQueries(['resumes', selectedJob]);
            queryClient.invalidateQueries(['rating-jobs']);
            
            // Force immediate refetch to ensure UI updates
            setTimeout(() => {
              queryClient.refetchQueries(['resumes', selectedJob]);
              queryClient.refetchQueries(['rating-jobs']);
            }, 500); // Small delay to ensure backend processing is complete
            
            setShowJobMonitor(false);
            setCurrentRatingJob(null);
            setSelectedResumes([]);
          }}
          onClose={() => {
            setShowJobMonitor(false);
            // Don't clear currentRatingJob in case user wants to check later
          }}
          onCancel={(jobId) => {
            // Job was cancelled
            queryClient.invalidateQueries(['resumes']);
            queryClient.invalidateQueries(['resumes', selectedJob]);
            queryClient.invalidateQueries(['rating-jobs']);
            queryClient.refetchQueries(['resumes', selectedJob]);
            setShowJobMonitor(false);
            setCurrentRatingJob(null);
          }}
          onRetry={(jobId, result) => {
            // Job was retried synchronously
            queryClient.invalidateQueries(['resumes']);
            queryClient.invalidateQueries(['resumes', selectedJob]);
            queryClient.invalidateQueries(['rating-jobs']);
            queryClient.refetchQueries(['resumes', selectedJob]);
            setShowJobMonitor(false);
            setCurrentRatingJob(null);
            setSelectedResumes([]);
          }}
        />
      )}

      {/* Rating Jobs List Modal */}
      {showJobsList && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Rating Jobs History</h2>
              <button
                onClick={() => setShowJobsList(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
              <RatingJobsList
                onJobSelect={(job, results) => {
                  if (job.status === 'completed' && results) {
                    toast.success(`Job completed: ${results.successful}/${results.total_processed} resumes rated.`);
                    queryClient.invalidateQueries(['resumes']);
                    queryClient.invalidateQueries(['rating-jobs']);
                  } else if (job.status === 'in_progress' || job.status === 'pending') {
                    setCurrentRatingJob(job.id);
                    setShowJobMonitor(true);
                  } else if (job.status === 'failed') {
                    toast.error(`Job failed: ${job.error_message || 'Unknown error'}`);
                  }
                  setShowJobsList(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Shortlisted Candidates Management Modal */}
      {showShortlistedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Manage Shortlisted Candidates</h2>
              <button
                onClick={() => setShowShortlistedModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Interview Selection for Link Generation */}
              <div className="mb-4 p-4 bg-green-50 rounded-lg">
                <h4 className="text-sm font-medium text-green-900 mb-3">Interview Link Generation</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select Interview
                    </label>
                    <select
                      value={selectedInterviewForLinks}
                      onChange={(e) => setSelectedInterviewForLinks(e.target.value)}
                      className="input-field text-sm"
                    >
                      <option value="">Select an interview...</option>
                      {availableInterviews.map((interview) => (
                        <option key={interview.id} value={interview.id}>
                          {interview.title} - {interview.status}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link Expiry (hours)
                    </label>
                    <input
                      type="number"
                      value={linkExpiryHours}
                      onChange={(e) => setLinkExpiryHours(parseInt(e.target.value))}
                      min="1"
                      max="168"
                      className="input-field text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Need a new interview?
                    </label>
                    <button
                      onClick={() => setShowCreateInterviewModal(true)}
                      className="btn-secondary text-sm w-full flex items-center justify-center space-x-1"
                      title="Create a new interview"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Create Interview</span>
                    </button>
                  </div>
                </div>
                <p className="text-xs text-green-600 mt-2">
                  Generate unique interview links for all candidates, then send personalized emails.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 mb-6">
                <button
                  onClick={async () => {
                    // Ensure we have available interviews loaded and first one selected
                    await loadAvailableInterviews();
                    setShowGenerateLinksModal(true);
                  }}
                  disabled={!selectedInterviewForLinks || candidatesNeedingLinksCount === 0}
                  className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                  title={
                    !selectedInterviewForLinks 
                      ? "Please select an interview first"
                      : candidatesNeedingLinksCount === 0 
                        ? "All candidates already have active links for this interview"
                        : `Generate interview links for ${candidatesNeedingLinksCount} candidates`
                  }
                >
                  <LinkIcon className="h-4 w-4" />
                  <span>
                    Generate Interview Links for All
                    {selectedInterviewForLinks && candidatesNeedingLinksCount !== filteredShortlistedCandidates.length && (
                      <span className="ml-1 text-sm">
                        ({candidatesNeedingLinksCount}/{filteredShortlistedCandidates.length})
                      </span>
                    )}
                  </span>
                </button>
                
                {/* <button
                  onClick={() => handleSendEmails(selectedInterviewForLinks, linkExpiryHours)}
                  disabled={isEmailOperationLoading || sendEmailsMutation.isLoading}
                  className="btn-secondary flex items-center space-x-2 disabled:opacity-50"
                  title="Send emails only to candidates with generated interview links"
                >
                  <EnvelopeIcon className="h-4 w-4" />
                  <span>Send Emails to All with Links</span>
                </button> */}
                
                {/* Commented out as requested
                <button
                  onClick={handleLinkCandidates}
                  disabled={linkCandidatesMutation.isLoading}
                  className="btn-secondary flex items-center space-x-2 disabled:opacity-50"
                >
                  <LinkIcon className="h-4 w-4" />
                  <span>Link Candidates</span>
                </button>
                */}
                
                {/* <button
                  onClick={openScheduleModal}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <CalendarIcon className="h-4 w-4" />
                  <span>Schedule Interview</span>
                </button> */}
              </div>

              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search candidates by name or email..."
                    value={shortlistedSearch}
                    onChange={(e) => setShortlistedSearch(e.target.value)}
                    className="input-field pl-10"
                  />
                </div>
              </div>

              {/* Candidates List */}
              <div className="overflow-y-auto max-h-80 border border-gray-200 rounded-lg scrollable-table">
                {isLoadingShortlisted ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  </div>
                ) : filteredShortlistedCandidates.length > 0 ? (
                  <div className="overflow-x-auto pb-4">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Candidate
                          </th>
                          {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email Status
                          </th> */}
                          {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Login Status
                          </th> */}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Active Links
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Used Links
                          </th>
                          {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email Attempts
                          </th> */}
                          {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Email
                          </th> */}
                          {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Interview Status
                          </th> */}
                          <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Interview Link
                          </th>
                          {/* <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th> */}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredShortlistedCandidates.map((candidate) => (
                          <tr key={candidate.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <DocumentIcon className="h-8 w-8 text-gray-400 mr-3" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {candidate.status === 'pending' ? 'Not Rated' : (candidate.candidate_name || 'Unknown')}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {candidate.candidate_email === 'N/A' ? (
                                      <span className="text-red-500">No Email</span>
                                    ) : (
                                      candidate.candidate_email || 'Unknown'
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {/* <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                {getEmailStatusIcon(candidate)}
                                <span className="text-sm text-gray-600">
                                  {candidate.email_status || 'Not sent'}
                                </span>
                              </div>
                            </td> */}
                            {/* <td className="px-6 py-4 whitespace-nowrap">
                              {getCandidateLoginStatus(candidate)}
                            </td> */}
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                {candidate.active_links_count || 0}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                                {candidate.used_links_count || 0}
                              </span>
                            </td>
                            {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {candidate.email_attempts || 0}
                            </td> */}
                            {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {candidate.last_email_attempt 
                                ? new Date(candidate.last_email_attempt).toLocaleDateString()
                                : 'Never'
                              }
                            </td> */}
                            {/* <td className="px-6 py-4 whitespace-nowrap">
                              {candidate.interview_scheduled ? (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" />
                                  Scheduled
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                                  <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                  Not Scheduled
                                </span>
                              )}
                            </td> */}
                            <td className="px-6 py-4 whitespace-nowrap text-center text-sm">
                              <button
                                onClick={async () => {
                                  setSelectedCandidateForLink(candidate);
                                  setShowSingleLinkModal(true);
                                  await loadAvailableInterviews();
                                  loadCandidateLinks(candidate.id);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 border border-blue-300 rounded hover:bg-blue-50"
                              >
                                Generate Link
                              </button>
                            </td>
                            {/* <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => handleResendEmail(candidate.id, selectedInterviewForLinks, linkExpiryHours)}
                                disabled={
                                  candidate.candidate_email === 'N/A' || 
                                  candidate.candidate_email === null ||
                                  resendEmailMutation.isLoading ||
                                  (candidate.active_links_count || 0) === 0
                                }
                                className="text-primary-600 hover:text-primary-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                title={candidate.candidate_email === 'N/A' || candidate.candidate_email === null ? 'No email available' : (candidate.active_links_count || 0) === 0 ? 'No active interview links' : 'Resend email with interview link'}
                              >
                                <EnvelopeIcon className="h-4 w-4" />
                              </button>
                            </td> */}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No shortlisted candidates found</p>
                    <p className="text-sm text-gray-400">
                      {shortlistedSearch ? 'Try adjusting your search terms' : 'Shortlist some candidates first'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Interview Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Schedule Interview</h3>
              <button
                onClick={() => setShowScheduleModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Interview
                </label>
                <select
                  value={selectedInterview}
                  onChange={(e) => setSelectedInterview(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select an interview...</option>
                  {availableInterviews.map((interview) => (
                    <option key={interview.id} value={interview.id}>
                      {interview.title} - {interview.status}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800">
                  This will create interview sessions for all linked shortlisted candidates.
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Only candidates who have joined the platform will be scheduled.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleInterview}
                disabled={!selectedInterview || scheduleInterviewMutation.isLoading}
                className="btn-primary disabled:opacity-50"
              >
                {scheduleInterviewMutation.isLoading ? 'Scheduling...' : 'Schedule Interview'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Interview Links Modal */}
      {showGenerateLinksModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Generate Interview Links</h3>
              <button
                onClick={() => {
                  setShowGenerateLinksModal(false);
                  setGeneratedLinks([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Interview Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Interview
                </label>
                <select
                  value={selectedInterviewForLinks}
                  onChange={(e) => setSelectedInterviewForLinks(e.target.value)}
                  className="input-field"
                >
                  <option value="">Select an interview...</option>
                  {availableInterviews.map((interview) => (
                    <option key={interview.id} value={interview.id}>
                      {interview.title} - {interview.status}
                    </option>
                  ))}
                </select>
              </div>

              {/* Link Expiry */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link Expiry (hours)
                </label>
                <input
                  type="number"
                  value={linkExpiryHours}
                  onChange={(e) => setLinkExpiryHours(parseInt(e.target.value))}
                  min="1"
                  max="168"
                  className="input-field w-32"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Links will expire after {linkExpiryHours} hours (max 7 days)
                </p>
              </div>

              {/* Candidates Preview */}
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800 font-medium">
                  This will generate unique interview links for {candidatesNeedingLinksCount} shortlisted candidates
                  {candidatesNeedingLinksCount !== filteredShortlistedCandidates.length && (
                    <span className="text-blue-600">
                      {' '}({filteredShortlistedCandidates.length - candidatesNeedingLinksCount} already have active links)
                    </span>
                  )}:
                </p>
                <div className="mt-2 max-h-32 overflow-y-auto">
                  {candidatesNeedingLinks.slice(0, 5).map((candidate, index) => (
                    <p key={index} className="text-xs text-blue-600">
                      • {candidate.candidate_name} ({candidate.candidate_email})
                    </p>
                  ))}
                  {candidatesNeedingLinks.length > 5 && (
                    <p className="text-xs text-blue-600">
                      ... and {candidatesNeedingLinks.length - 5} more candidates
                    </p>
                  )}
                  {candidatesNeedingLinks.length === 0 && candidatesNeedingLinksCount === 0 && (
                    <p className="text-xs text-blue-600">
                      All candidates already have active links for this interview.
                    </p>
                  )}
                </div>
              </div>

              {/* Generated Links */}
              {generatedLinks.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-3">
                    Generated Links ({generatedLinks.length})
                  </h4>
                  <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                    {generatedLinks.map((linkData, index) => (
                      <div key={index} className="p-3 border-b border-gray-100 last:border-b-0">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-900">
                              {linkData.candidateName || 'Unknown Candidate'}
                            </p>
                            <p className="text-sm text-gray-500">
                              {linkData.candidateEmail || 'No email'}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(linkData.full_url);
                                toast.success('Link copied to clipboard!');
                              }}
                              className="btn-secondary text-xs px-2 py-1 flex-shrink-0"
                              title="Copy interview link"
                            >
                              Copy
                            </button>
                            <code 
                              className="text-xs bg-gray-100 px-2 py-1 rounded flex-1 truncate cursor-pointer hover:bg-gray-200"
                              title={linkData.full_url}
                              onClick={() => {
                                navigator.clipboard.writeText(linkData.full_url);
                                toast.success('Link copied to clipboard!');
                              }}
                            >
                              {linkData.full_url.length > 50 
                                ? `${linkData.full_url.substring(0, 47)}...` 
                                : linkData.full_url}
                            </code>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Bulk copy all links */}
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={() => {
                        const allLinks = generatedLinks.map(link => 
                          `${link.candidateName}: ${link.full_url}`
                        ).join('\n');
                        navigator.clipboard.writeText(allLinks);
                        toast.success('All links copied to clipboard!');
                      }}
                      className="btn-secondary text-sm"
                    >
                      Copy All Links
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowGenerateLinksModal(false);
                  setGeneratedLinks([]);
                }}
                className="btn-secondary"
              >
                Close
              </button>
              <button
                onClick={handleGenerateInterviewLinks}
                disabled={
                  !selectedInterviewForLinks || 
                  isGeneratingLinks || 
                  candidatesNeedingLinksCount === 0
                }
                className="btn-primary disabled:opacity-50"
              >
                {isGeneratingLinks 
                  ? `Generating... (${generatedLinks.length}/${candidatesNeedingLinksCount})`
                  : candidatesNeedingLinksCount === 0
                    ? 'All Candidates Have Active Links'
                    : `Generate ${candidatesNeedingLinksCount} Links`
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Candidate Interview Link Modal */}
      {showSingleLinkModal && selectedCandidateForLink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Interview Links for {selectedCandidateForLink.candidate_name}
              </h3>
              <button
                onClick={() => {
                  setShowSingleLinkModal(false);
                  setSelectedCandidateForLink(null);
                  setCandidateLinks([]);
                  setSingleLinkInterview('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Candidate Info */}
            <div className="bg-gray-50 p-3 rounded-lg mb-4">
              <p className="text-sm"><strong>Candidate:</strong> {selectedCandidateForLink.candidate_name}</p>
              <p className="text-sm"><strong>Email:</strong> {selectedCandidateForLink.candidate_email}</p>
              <p className="text-sm"><strong>Resume File:</strong> {selectedCandidateForLink.filename}</p>
            </div>

            {/* Generate New Link Section */}
            <div className="border border-gray-200 rounded-lg p-4 mb-6">
              <h4 className="text-md font-medium text-gray-900 mb-3">Generate New Interview Link</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Interview
                  </label>
                  <select
                    value={singleLinkInterview}
                    onChange={(e) => setSingleLinkInterview(e.target.value)}
                    className="input-field"
                  >
                    <option value="">Select an interview...</option>
                    {availableInterviews.map((interview) => (
                      <option key={interview.id} value={interview.id}>
                        {interview.title} - {interview.status}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry (hours)
                  </label>
                  <input
                    type="number"
                    value={singleLinkExpiry}
                    onChange={(e) => setSingleLinkExpiry(parseInt(e.target.value))}
                    min="1"
                    max="168"
                    className="input-field"
                  />
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={handleGenerateSingleLink}
                    disabled={
                      !singleLinkInterview || 
                      generateSingleLinkMutation.isLoading ||
                      candidateLinks.some(link => 
                        link.interview?.id === singleLinkInterview && 
                        link.status === 'active'
                      )
                    }
                    className="btn-primary disabled:opacity-50 w-full"
                    title={
                      candidateLinks.some(link => 
                        link.interview?.id === singleLinkInterview && 
                        link.status === 'active'
                      ) 
                        ? 'An active link already exists for this interview' 
                        : ''
                    }
                  >
                    {generateSingleLinkMutation.isLoading ? 'Generating...' : 
                     candidateLinks.some(link => 
                       link.interview?.id === singleLinkInterview && 
                       link.status === 'active'
                     ) ? 'Active Link Exists' : 'Generate Link'}
                  </button>
                </div>
              </div>
            </div>

            {/* Existing Links */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-md font-medium text-gray-900">Existing Interview Links</h4>
                <button
                  onClick={() => loadCandidateLinks(selectedCandidateForLink.id)}
                  className="btn-secondary text-sm"
                >
                  Refresh
                </button>
              </div>
              
              {candidateLinks.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {candidateLinks.map((link, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="font-medium text-sm">
                              {link.interview?.title || 'Unknown Interview'}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              link.status === 'active' ? 'bg-green-100 text-green-800' :
                              link.status === 'used' ? 'bg-blue-100 text-blue-800' :
                              link.status === 'expired' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {link.status?.toUpperCase()}
                            </span>
                          </div>
                          
                          <p className="text-xs text-gray-500 mb-2">
                            Created: {new Date(link.created_at).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500 mb-2">
                            Expires: {new Date(link.expires_at).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500 mb-3">
                            Access Count: {link.access_count || 0}
                          </p>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                const fullUrl = `${window.location.origin}${link.url}`;
                                navigator.clipboard.writeText(fullUrl);
                                toast.success('Link copied to clipboard!');
                              }}
                              className="btn-secondary text-xs px-2 py-1 flex-shrink-0"
                              title="Copy interview link"
                            >
                              Copy
                            </button>
                            <code 
                              className="text-xs bg-gray-100 px-2 py-1 rounded flex-1 truncate cursor-pointer hover:bg-gray-200"
                              title={`${window.location.origin}${link.url}`}
                              onClick={() => {
                                const fullUrl = `${window.location.origin}${link.url}`;
                                navigator.clipboard.writeText(fullUrl);
                                toast.success('Link copied to clipboard!');
                              }}
                            >
                              {link.url.length > 50 
                                ? `${link.url.substring(0, 47)}...` 
                                : link.url}
                            </code>
                          </div>
                        </div>
                        
                        <div className="flex flex-col space-y-2 ml-4">
                          {link.status === 'used' || link.status === 'expired' ? (
                            <button
                              onClick={() => handleReopenLink(link.id)}
                              disabled={reopenLinkMutation.isLoading}
                              className="btn-secondary text-xs disabled:opacity-50"
                            >
                              Reopen
                            </button>
                          ) : null}
                          
                          {link.status === 'active' && (
                            <button
                              onClick={() => handleDeleteLink(link.id)}
                              disabled={deleteLinkMutation.isLoading}
                              className="text-red-600 hover:text-red-800 text-xs px-2 py-1 border border-red-300 rounded disabled:opacity-50"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <LinkIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p>No interview links found for this candidate</p>
                  <p className="text-sm">Generate a new link above to get started</p>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  setShowSingleLinkModal(false);
                  setSelectedCandidateForLink(null);
                  setCandidateLinks([]);
                  setSingleLinkInterview('');
                }}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && resumeToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-medium text-gray-900">Delete Resume</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 mb-2">
                Are you sure you want to delete this resume?
              </p>
              <div className="bg-gray-50 p-3 rounded border">
                <p className="font-medium text-gray-900">
                  {resumeToDelete.status === 'pending' ? 'Not Rated' : (resumeToDelete.candidate_name || 'Unknown Candidate')}
                </p>
                <p className="text-sm text-gray-600">{resumeToDelete.filename}</p>
                <p className="text-xs text-gray-500">
                  Uploaded: {new Date(resumeToDelete.created_at).toLocaleDateString()}
                </p>
              </div>
              <p className="text-sm text-red-600 mt-2">
                This action cannot be undone. The resume file and all associated data will be permanently deleted.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={handleDeleteCancel}
                disabled={isDeleting}
                className="btn-secondary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Deleting...' : 'Delete Resume'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Interview Modal */}
      {showCreateInterviewModal && (
        <CreateInterviewModal
          onClose={() => setShowCreateInterviewModal(false)}
          onSubmit={(data) => createInterviewMutation.mutate(data)}
          jobDescriptions={jobsData?.job_descriptions || []}
          isLoading={createInterviewMutation.isLoading}
          defaultJobId={selectedJob}
        />
      )}
    </div>
  );
};

export default ResumeManagement;